import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

// Define the response types for email functions
interface VerifyEmailLinkResponse {
  success: boolean;
  userId?: string;
  targetRoute: string;
  data?: Record<string, unknown>;
  customToken?: string;
  message?: string;
}

interface GenerateEmailLinkResponse {
  success: boolean;
  link: string;
  token: string;
  expiresAt: number;
}

interface EmailFunctionResponse {
  success: boolean;
  progressData?: unknown;
  error?: string;
  skipped?: boolean;
}

// Lazy initialization of callable functions to avoid circular dependency
let generateSecureEmailLinkFunction: ReturnType<typeof httpsCallable<{
  targetRoute: string;
  userId?: string;
  expiresIn?: number;
  data?: Record<string, unknown>;
}, GenerateEmailLinkResponse>> | null = null;

let verifyEmailLinkFunction: ReturnType<typeof httpsCallable<
  { token: string },
  VerifyEmailLinkResponse
>> | null = null;

let sendDay1ActivationEmailFunction: ReturnType<typeof httpsCallable<
  Record<string, never>,
  EmailFunctionResponse
>> | null = null;

let sendPersonalizedProgressEmailFunction: ReturnType<typeof httpsCallable<
  Record<string, never>,
  EmailFunctionResponse
>> | null = null;

const getGenerateSecureEmailLinkFunction = () => {
  if (!generateSecureEmailLinkFunction) {
    generateSecureEmailLinkFunction = httpsCallable<
      {
        targetRoute: string;
        userId?: string;
        expiresIn?: number;
        data?: Record<string, unknown>;
      },
      GenerateEmailLinkResponse
    >(functions, 'generateSecureEmailLink');
  }
  return generateSecureEmailLinkFunction;
};

const getVerifyEmailLinkFunction = () => {
  if (!verifyEmailLinkFunction) {
    verifyEmailLinkFunction = httpsCallable<
      { token: string },
      VerifyEmailLinkResponse
    >(functions, 'verifyEmailLink');
  }
  return verifyEmailLinkFunction;
};

const getSendDay1ActivationEmailFunction = () => {
  if (!sendDay1ActivationEmailFunction) {
    sendDay1ActivationEmailFunction = httpsCallable<
      Record<string, never>,
      EmailFunctionResponse
    >(functions, 'sendDay1ActivationEmail');
  }
  return sendDay1ActivationEmailFunction;
};

const getSendPersonalizedProgressEmailFunction = () => {
  if (!sendPersonalizedProgressEmailFunction) {
    sendPersonalizedProgressEmailFunction = httpsCallable<
      Record<string, never>,
      EmailFunctionResponse
    >(functions, 'sendPersonalizedProgressEmail');
  }
  return sendPersonalizedProgressEmailFunction;
};

/**
 * Generate a secure email link that can authenticate users and redirect to protected routes
 */
export const generateSecureEmailLink = async (
  targetRoute: string,
  userId?: string,
  expiresIn?: number,
  data?: Record<string, unknown>
): Promise<GenerateEmailLinkResponse> => {
  try {
    const fn = getGenerateSecureEmailLinkFunction();
    const result = await fn({
      targetRoute,
      userId,
      expiresIn,
      data
    });

    if (!result.data.success) {
      throw new Error('Failed to generate secure email link');
    }

    return result.data;
  } catch (error) {
    console.error('Error generating secure email link:', error);
    throw error;
  }
};

/**
 * Verify an email link token and get the target route and auth info
 */
export const verifyEmailLink = async (token: string): Promise<VerifyEmailLinkResponse> => {
  try {
    const fn = getVerifyEmailLinkFunction();
    const result = await fn({ token });

    if (!result.data.success) {
      throw new Error(result.data.message || 'Email link verification failed');
    }

    return result.data;
  } catch (error) {
    console.error('Error verifying email link:', error);
    throw error;
  }
};

/**
 * Send Day 1 activation email to the current user
 */
export const sendDay1ActivationEmail = async (): Promise<EmailFunctionResponse> => {
  try {
    const fn = getSendDay1ActivationEmailFunction();
    const result = await fn({});
    return result.data;
  } catch (error) {
    console.error('Error sending Day 1 activation email:', error);
    throw error;
  }
};

/**
 * Send personalized progress email to the current user
 */
export const sendPersonalizedProgressEmail = async (): Promise<EmailFunctionResponse> => {
  try {
    const fn = getSendPersonalizedProgressEmailFunction();
    const result = await fn({});
    return result.data;
  } catch (error) {
    console.error('Error sending personalized progress email:', error);
    throw error;
  }
};

// Export all email service functions
export const emailService = {
  generateSecureEmailLink,
  verifyEmailLink,
  sendDay1ActivationEmail,
  sendPersonalizedProgressEmail
};